import React, { useState, useEffect } from 'react'
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material'
import { Check as CheckIcon, Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material'
import { SideCard } from './SideCard'
import { FormInput } from '../ui'
import { themeColors } from '../../app/layout/theme'

interface DescriptionSectionProps {
  value: string | null | undefined
  onSave: (val: string) => void | Promise<void>
  isPending?: boolean
  canEdit?: boolean
  placeholder?: string
  emptyText?: string
  rows?: number
}

export function DescriptionSection({
  value,
  onSave,
  isPending = false,
  canEdit = true,
  placeholder = 'Add a description...',
  emptyText = 'No description added yet.',
  rows = 3,
}: DescriptionSectionProps) {
  const [editing, setEditing] = useState(false)
  const [tempVal, setTempVal] = useState('')

  useEffect(() => {
    if (!isPending) {
      setEditing(false)
    }
  }, [value, isPending])

  const handleEdit = () => {
    setTempVal(value ?? '')
    setEditing(true)
  }

  const handleSave = async () => {
    await onSave(tempVal.trim())
  }

  const handleCancel = () => {
    setEditing(false)
  }

  const actionButton = !editing && canEdit ? (
    <Tooltip title="Edit description">
      <IconButton
        size="small"
        onClick={handleEdit}
        sx={{
          color: '#9ca3af',
          '&:hover': {
            color: themeColors.primary,
            bgcolor: 'rgba(99, 102, 241, 0.08)',
          },
        }}
      >
        <EditIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  ) : undefined

  return (
    <SideCard title="Description" action={actionButton}>
      {editing ? (
        <Box>
          <FormInput
            value={tempVal}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTempVal(e.target.value)}
            multiline
            rows={rows}
            fullWidth
            autoFocus
            placeholder={placeholder}
            sx={{ '& .MuiInputBase-root': { fontSize: '12.5px' } }}
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button
              size="small"
              variant="contained"
              disabled={isPending}
              onClick={handleSave}
              sx={{
                borderRadius: '6px',
                fontSize: '11px',
                textTransform: 'none',
                background: themeColors.primary,
                '&:hover': { background: themeColors.primaryDark },
              }}
            >
              {isPending ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : 'Save'}
            </Button>
            <Button
              size="small"
              onClick={handleCancel}
              sx={{ borderRadius: '6px', fontSize: '11px', textTransform: 'none' }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      ) : value ? (
        <Typography
          fontSize="12.5px"
          color="text.primary"
          sx={{ lineHeight: 1.65, whiteSpace: 'pre-wrap' }}
        >
          {value}
        </Typography>
      ) : (
        <Typography
          fontSize="12px"
          color="text.disabled"
          sx={{ fontStyle: 'italic' }}
        >
          {emptyText}
        </Typography>
      )}
    </SideCard>
  )
}
